/* Geethas — Easify + Currency Converter+ compatibility bridge
   Version 3.8

   IMPORTANT INSTALLATION ORDER
   Load this asset in <head> BEFORE {{ content_for_header }} and without defer.
   That lets it normalise Indian thousands separators before Currency Converter+
   observes Easify/cart text.

   Runs only on geethas.no.
   No fixed exchange rate is stored. The script derives the current display rate
   from the raw INR product page and Currency Converter+'s live NOK product price.

   Display-only safeguards:
   - does not alter Easify's numeric surcharge configuration
   - does not dispatch change/input events
   - does not alter Shopify cart calculations or checkout currency
   - repairs only Easify option-property text in the cart
   - never rewrites Easify dropdown input values or selected option state
   - never rewrites cart item prices, unit prices, totals, discounts, or checkout values
*/
(() => {
  'use strict';

  const host = window.location.hostname.toLowerCase();
  if (host !== 'geethas.no' && !host.endsWith('.geethas.no')) return;

  const VERSION = '3.8';
  const DEBUG = new URLSearchParams(window.location.search)
    .get('geethasCurrencyDebug') === '1';
  const RATE_MIN = 5;
  const RATE_MAX = 20;
  const PRODUCT_SCOPE = '.tpo_option-set-wrapper, [class*="tpo_"], [id^="tpo_"]';
  const CART_ROOT = 'cart-items-component, .cart-items-component, cart-drawer, .cart-drawer, #CartDrawer';
  const CART_FORM = '#cart-form';

  const state = {
    rate: null,
    rateSource: null,
    rawProductInr: null,
    liveProductNok: null,
    productTimer: null,
    cartTimer: null,
    cartBusy: false,
    productBusy: false,
    observedProductRoots: new WeakSet(),
    observedPopupRoots: new WeakSet(),
    observedCartRoots: new WeakSet(),
  };

  function log(...args) {
    if (DEBUG) console.log(`[Geethas currency V${VERSION}]`, ...args);
  }

  function validRate(value) {
    return Number.isFinite(value) && value >= RATE_MIN && value <= RATE_MAX;
  }

  function parseInr(raw) {
    let value = String(raw || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[^\d.,-]/g, '')
      .trim();
    if (!value) return null;

    // Easify/Currency Converter+ use Indian whole-rupee prices. A separator
    // followed by exactly three digits is a thousands separator, not decimals.
    if (/^-?\d{1,3}(?:[.,]\d{3})+$/.test(value)) {
      value = value.replace(/[.,]/g, '');
    } else if (/^-?\d+[.,]\d{2}$/.test(value)) {
      value = value.replace(',', '.');
    } else {
      value = value.replace(/[.,]/g, '');
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function parseNok(raw) {
    let value = String(raw || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[^\d.,-]/g, '')
      .trim();
    if (!value) return null;

    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');
    const last = Math.max(lastComma, lastDot);
    if (last >= 0 && value.length - last - 1 === 2) {
      value = `${value.slice(0, last).replace(/[.,]/g, '')}.${value.slice(last + 1)}`;
    } else {
      value = value.replace(/[.,]/g, '');
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function firstInr(text) {
    const value = String(text || '');
    const patterns = [
      /(?:₹|Rs\.?)\s*([\d][\d.,\s]*)\s*(?:INR\b)?/i,
      /([\d][\d.,\s]*)\s*INR\b/i,
    ];
    for (const pattern of patterns) {
      const match = value.match(pattern);
      if (!match) continue;
      const amount = parseInr(match[1]);
      if (Number.isFinite(amount) && amount > 0) return amount;
    }
    return null;
  }

  function firstNok(text) {
    const value = String(text || '');
    const patterns = [
      /kr\s*([\d][\d.,\s]*)\s*NOK\b/i,
      /NOK\s*([\d][\d.,\s]*)/i,
    ];
    for (const pattern of patterns) {
      const match = value.match(pattern);
      if (!match) continue;
      const amount = parseNok(match[1]);
      if (Number.isFinite(amount) && amount > 0) return amount;
    }
    return null;
  }

  function amountText(amount) {
    const rounded = Math.round(Number(amount));
    return rounded.toLocaleString('en-US');
  }

  function nokText(amount) {
    return `kr ${amountText(amount)} NOK`;
  }

  function convertedInrText(inr) {
    if (!validRate(state.rate) || !Number.isFinite(inr)) return null;
    return nokText(inr / state.rate);
  }

  // ---------------------------------------------------------------------------
  // EARLY PRE-PARSER: remove Indian thousands separators only inside INR strings.
  // Currency Converter+ otherwise parses ₹1.999 as ₹1 and leaves .999 INR behind.
  // ---------------------------------------------------------------------------

  function normaliseInrThousands(text) {
    let output = String(text || '');
    output = output.replace(
      /((?:₹|Rs\.?)\s*)(\d{1,3}(?:[.,]\d{3})+)(\s*INR\b)/gi,
      (_whole, prefix, number, suffix) => `${prefix}${number.replace(/[.,]/g, '')}${suffix}`
    );
    output = output.replace(
      /(\b)(\d{1,3}(?:[.,]\d{3})+)(\s*INR\b)/gi,
      (_whole, boundary, number, suffix) => `${boundary}${number.replace(/[.,]/g, '')}${suffix}`
    );
    return output;
  }

  function relevantForEarlyNormalisation(element) {
    if (!(element instanceof Element)) return false;
    return Boolean(
      element.closest(PRODUCT_SCOPE) ||
      element.closest(CART_FORM) ||
      element.matches(PRODUCT_SCOPE) ||
      element.matches(CART_FORM)
    );
  }

  function normaliseNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const parent = node.parentElement;
      if (!parent || !relevantForEarlyNormalisation(parent)) return;
      const before = node.nodeValue || '';
      if (!/(?:₹|INR)/i.test(before)) return;
      const after = normaliseInrThousands(before);
      if (after !== before) node.nodeValue = after;
      return;
    }

    if (!(node instanceof Element)) return;
    if (!relevantForEarlyNormalisation(node) && !node.querySelector(PRODUCT_SCOPE) && !node.querySelector(CART_FORM)) {
      return;
    }

    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(normaliseNode);

    node.querySelectorAll('option, [aria-label], [aria-valuetext], [data-label], [title]')
      .forEach((element) => {
        for (const name of ['aria-label', 'aria-valuetext', 'data-label', 'title']) {
          const before = element.getAttribute(name);
          if (!before || !/(?:₹|INR)/i.test(before)) continue;
          const after = normaliseInrThousands(before);
          if (after !== before) element.setAttribute(name, after);
        }
        if (element instanceof HTMLOptionElement) {
          const before = element.textContent || '';
          const after = normaliseInrThousands(before);
          if (after !== before) element.textContent = after;
        }
      });
  }

  const earlyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        normaliseNode(mutation.target);
      } else {
        mutation.addedNodes.forEach(normaliseNode);
      }
    }
  });

  if (document.documentElement) {
    earlyObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  // ---------------------------------------------------------------------------
  // Live rate: raw server-rendered INR product price / converted visible NOK price.
  // ---------------------------------------------------------------------------

  function mainProductRoot(root = document) {
    return root.querySelector('[id^="ProductInformation-"]') ||
      root.querySelector('.product-information') ||
      root.querySelector('main');
  }

  function mainProductPriceText(root = document) {
    const productRoot = mainProductRoot(root);
    if (!productRoot) return '';
    const price = productRoot.querySelector('product-price') ||
      productRoot.querySelector('.price') ||
      productRoot.querySelector('[class*="price" i]');
    return price ? (price.textContent || '') : '';
  }

  function setRate(inr, nok, source) {
    const rate = inr / nok;
    if (!validRate(rate)) return false;
    state.rate = rate;
    state.rateSource = source;
    state.rawProductInr = inr;
    state.liveProductNok = nok;
    log('Live rate derived:', rate, source);
    scheduleProductRepair(0);
    scheduleCartRepair(80);
    return true;
  }

  async function deriveRateFromProductPage() {
    try {
      const response = await fetch(window.location.href, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTML ${response.status}`);
      const html = await response.text();
      const rawDocument = new DOMParser().parseFromString(html, 'text/html');
      const rawInr = firstInr(mainProductPriceText(rawDocument));
      if (!Number.isFinite(rawInr)) throw new Error('raw INR product price not found');

      for (let attempt = 0; attempt < 60; attempt += 1) {
        const liveNok = firstNok(mainProductPriceText(document));
        if (Number.isFinite(liveNok) && setRate(rawInr, liveNok, 'raw product HTML / live NOK product price')) {
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      throw new Error('live NOK product price not found');
    } catch (error) {
      log('Primary rate derivation failed:', error);
      deriveRateFromCleanEasifyPairs();
    }
  }

  function deriveRateFromCleanEasifyPairs() {
    const candidates = document.querySelectorAll(`${PRODUCT_SCOPE} input, ${PRODUCT_SCOPE} option`);
    for (const element of candidates) {
      const raw = element instanceof HTMLOptionElement
        ? `${element.value || ''} ${element.textContent || ''}`
        : String(element.value || '');
      const inr = firstInr(raw);
      if (!Number.isFinite(inr)) continue;
      const container = element.closest('.tpo_option-container, .tpo_option-set-wrapper, [class*="tpo_"]');
      if (!container) continue;
      const nok = firstNok(container.textContent || '');
      if (!Number.isFinite(nok) || nok < 1) continue;
      if (setRate(inr, nok, 'clean Easify option pair')) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Product option repair — exact Easify tpo_ DOM, never the full page.
  // ---------------------------------------------------------------------------

  function optionNameFromRaw(raw) {
    return String(raw || '')
      .replace(/\s*[\[(]\s*\+?\s*(?:₹|Rs\.|kr).*$/i, '')
      .trim();
  }

  function repairMalformedCurrencyText(text) {
    let output = String(text || '');
    if (!validRate(state.rate)) return output;

    // Currency Converter+ malformed thousands: kr0.10NOK.999 INR.
    output = output.replace(
      /kr\s*([\d]+(?:[.,]\d+)?)\s*NOK\s*[.,]\s*(\d{3})\s*INR\b/gi,
      (whole, partialRaw, tail) => {
        const partialNok = parseNok(partialRaw);
        if (!Number.isFinite(partialNok)) return whole;
        const leadingInr = Math.max(1, Math.round(partialNok * state.rate));
        const fullInr = leadingInr * 1000 + Number(tail);
        return convertedInrText(fullInr) || whole;
      }
    );

    // Untouched ₹190 INR / ₹1.999 INR forms.
    output = output.replace(
      /(?:₹|Rs\.?)\s*([\d][\d.,\s]*)\s*INR\b/gi,
      (whole, raw) => convertedInrText(parseInr(raw)) || whole
    );
    output = output.replace(
      /([\d][\d.,\s]*)\s*INR\b/gi,
      (whole, raw) => convertedInrText(parseInr(raw)) || whole
    );

    return output;
  }

  function setFixedText(element, text) {
    if (!(element instanceof Element) || !text) return;
    if (element.textContent !== text) element.textContent = text;
    element.classList.add('notranslate', 'geethas-currency-fixed');
    element.setAttribute('translate', 'no');
  }

  function repairChoiceLabels(root) {
    root.querySelectorAll('input.tpo_radio-button, input.tpo-checkbox-button')
      .forEach((input) => {
        const raw = String(input.value || '');
        const inr = firstInr(raw);
        if (!Number.isFinite(inr)) return;
        const name = optionNameFromRaw(raw);
        const converted = convertedInrText(inr);
        if (!name || !converted) return;
        const label = input.closest('label');
        const visible = label && label.querySelector('.tpo_option_type_label');
        if (visible) setFixedText(visible, `${name} (${converted})`);
      });
  }

  function repairNativeSelects(root) {
    root.querySelectorAll('select').forEach((select) => {
      [...select.options].forEach((option) => {
        const raw = `${option.textContent || ''} ${option.label || ''}`;
        if (!/(?:₹|INR)/i.test(raw)) return;
        const next = repairMalformedCurrencyText(option.textContent || '');
        if (next !== option.textContent) option.textContent = next;
        option.label = repairMalformedCurrencyText(option.label || option.textContent || '');
      });
    });
  }

  const displayOverlays = new WeakMap();

  function clearDisplayOverlay(control) {
    const overlay = displayOverlays.get(control);
    if (overlay) overlay.remove();
    displayOverlays.delete(control);
    control.classList.remove('geethas-currency-overlay-source');
  }

  function setDisplayOverlay(control, text) {
    const host = control.parentElement;
    if (!host || !text) return;

    host.classList.add('geethas-currency-overlay-host');
    let overlay = displayOverlays.get(control);
    if (!overlay || !overlay.isConnected) {
      overlay = document.createElement('span');
      overlay.className = 'geethas-currency-display-overlay notranslate';
      overlay.setAttribute('translate', 'no');
      control.insertAdjacentElement('afterend', overlay);
      displayOverlays.set(control, overlay);
    }

    if (overlay.textContent !== text) overlay.textContent = text;
    control.classList.add('geethas-currency-overlay-source');
  }

  function repairVisibleDisplayControls(root) {
    root.querySelectorAll(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, [role="combobox"]'
    ).forEach((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)) return;

      // Never replace control.value. Easify uses the original value to resolve
      // and change selections. Changing it makes the first selected option stick.
      const raw = String(control.value || '');

      // Only decorate display-style controls. Named editable customer fields are
      // measurements and must never be touched.
      const displayOnly = control.readOnly ||
        control.getAttribute('role') === 'combobox' ||
        !control.name ||
        /select|dropdown|search/i.test(control.className || '');
      if (!displayOnly) {
        clearDisplayOverlay(control);
        return;
      }

      if (!/(?:₹|INR|kr\s*[\d.,]+\s*NOK\s*[.,]\s*\d{3}\s*INR)/i.test(raw)) {
        clearDisplayOverlay(control);
        return;
      }

      const next = repairMalformedCurrencyText(raw);
      if (!next || next === raw) {
        clearDisplayOverlay(control);
        return;
      }

      setDisplayOverlay(control, next);
    });
  }

  function repairCurrencyTextNodes(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, textarea')) return NodeFilter.FILTER_REJECT;
        const text = node.nodeValue || '';
        return /(?:INR|₹|kr\s*[\d.,]+\s*NOK\s*[.,]\s*\d{3}\s*INR)/i.test(text)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const before = node.nodeValue || '';
      const after = repairMalformedCurrencyText(before);
      if (after !== before) {
        node.nodeValue = after;
        if (node.parentElement) {
          node.parentElement.classList.add('notranslate', 'geethas-currency-fixed');
          node.parentElement.setAttribute('translate', 'no');
        }
      }
    });
  }

  function productRoots() {
    const roots = new Set(document.querySelectorAll('.tpo_option-set-wrapper'));
    document.querySelectorAll('[class*="tpo_"][role="listbox"], [class*="tpo_"][role="menu"], body > [class*="tpo_"]')
      .forEach((element) => roots.add(element));
    return [...roots].filter((element) => element.isConnected && !element.closest(CART_FORM));
  }

  function repairProductOptions() {
    state.productTimer = null;
    if (state.productBusy || !validRate(state.rate)) return;
    state.productBusy = true;
    try {
      productRoots().forEach((root) => {
        repairChoiceLabels(root);
        repairNativeSelects(root);
        repairVisibleDisplayControls(root);
        repairCurrencyTextNodes(root);
        observeProductRoot(root);
      });
      deriveRateFromCleanEasifyPairs();
    } finally {
      state.productBusy = false;
    }
  }

  function scheduleProductRepair(delay = 50) {
    if (state.productTimer !== null) window.clearTimeout(state.productTimer);
    state.productTimer = window.setTimeout(repairProductOptions, delay);
  }

  function observeProductRoot(root) {
    if (!(root instanceof Element) || state.observedProductRoots.has(root)) return;
    state.observedProductRoots.add(root);
    const observer = new MutationObserver(() => {
      if (!state.productBusy) scheduleProductRepair(35);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  }

  // ---------------------------------------------------------------------------
  // Cart property repair only.
  //
  // IMPORTANT: Shopify /cart.js uses the store's base INR currency in this
  // setup. It must never be labelled as NOK. Currency Converter+ already
  // converts the actual cart prices and totals correctly, so this bridge leaves
  // every cart money element completely untouched.
  // ---------------------------------------------------------------------------

  function repairCartPropertyElement(propertyElement) {
    if (!(propertyElement instanceof Element)) return;

    const converter = propertyElement.querySelector('.currency-converter-wrapper-amount-box');
    if (converter) {
      const before = converter.textContent || '';
      const after = validRate(state.rate)
        ? repairMalformedCurrencyText(before)
        : normaliseInrThousands(before);
      if (after !== before) setFixedText(converter, after);
      return;
    }

    const walker = document.createTreeWalker(propertyElement, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style')) return NodeFilter.FILTER_REJECT;
        const value = node.nodeValue || '';
        return /(?:₹|INR|kr\s*[\d.,]+\s*NOK\s*[.,]\s*\d{3}\s*INR)/i.test(value)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const before = node.nodeValue || '';
      const after = validRate(state.rate)
        ? repairMalformedCurrencyText(before)
        : normaliseInrThousands(before);
      if (after !== before) {
        node.nodeValue = after;
        if (node.parentElement) {
          node.parentElement.classList.add('notranslate', 'geethas-currency-fixed');
          node.parentElement.setAttribute('translate', 'no');
        }
      }
    });
  }

  function repairCart() {
    state.cartTimer = null;
    if (state.cartBusy) return;
    const form = document.querySelector(CART_FORM);
    if (!(form instanceof Element)) return;

    state.cartBusy = true;
    try {
      form.querySelectorAll('.cart-items__property').forEach(repairCartPropertyElement);
    } finally {
      state.cartBusy = false;
    }
  }

  function scheduleCartRepair(delay = 160) {
    if (state.cartTimer !== null) window.clearTimeout(state.cartTimer);
    state.cartTimer = window.setTimeout(repairCart, delay);
  }

  function observeCartRoot(root) {
    if (!(root instanceof Element) || state.observedCartRoots.has(root)) return;
    state.observedCartRoots.add(root);
    const observer = new MutationObserver(() => {
      if (!state.cartBusy) scheduleCartRepair(140);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    scheduleCartRepair(80);
  }

  function discoverRoots(root = document) {
    if (!(root instanceof Document || root instanceof Element)) return;

    const productCandidates = [];
    if (root instanceof Element && root.matches('.tpo_option-set-wrapper')) productCandidates.push(root);
    if (root.querySelectorAll) productCandidates.push(...root.querySelectorAll('.tpo_option-set-wrapper'));
    productCandidates.forEach(observeProductRoot);

    const cartCandidates = [];
    if (root instanceof Element && root.matches(CART_ROOT)) cartCandidates.push(root);
    if (root.querySelectorAll) cartCandidates.push(...root.querySelectorAll(CART_ROOT));
    cartCandidates.forEach(observeCartRoot);
  }

  const discoveryObserver = new MutationObserver((mutations) => {
    let productChanged = false;
    let cartChanged = false;

    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        discoverRoots(node);
        if (node.matches(PRODUCT_SCOPE) || node.querySelector(PRODUCT_SCOPE)) productChanged = true;
        if (node.matches(CART_ROOT) || node.querySelector(CART_FORM) || node.querySelector(CART_ROOT)) cartChanged = true;
      });
    }

    if (productChanged) scheduleProductRepair(30);
    if (cartChanged) scheduleCartRepair(100);
  });

  function injectStyle() {
    if (document.getElementById('geethas-currency-fix-style')) return;
    const style = document.createElement('style');
    style.id = 'geethas-currency-fix-style';
    style.textContent = `
      .geethas-currency-fixed { white-space: nowrap; }
      .geethas-currency-overlay-host { position: relative !important; }
      .geethas-currency-overlay-source {
        color: transparent !important;
        text-shadow: none !important;
      }
      .geethas-currency-display-overlay {
        position: absolute;
        z-index: 3;
        top: 50%;
        left: 12px;
        right: 38px;
        transform: translateY(-50%);
        pointer-events: none;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: currentColor;
        line-height: 1.2;
      }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  function onInteraction(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest(PRODUCT_SCOPE)) {
      [20, 100, 260].forEach((delay) => window.setTimeout(() => scheduleProductRepair(0), delay));
    }
    if (target.closest(CART_ROOT) || target.closest('[data-cart-drawer], [href="/cart"]')) {
      [80, 250, 600].forEach((delay) => window.setTimeout(() => scheduleCartRepair(0), delay));
    }
  }

  function start() {
    injectStyle();
    normaliseNode(document.body);
    discoverRoots(document);

    discoveryObserver.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', onInteraction, true);
    document.addEventListener('change', onInteraction, true);

    deriveRateFromProductPage();
    [0, 120, 400, 1000].forEach((delay) => {
      window.setTimeout(() => {
        scheduleProductRepair(0);
        scheduleCartRepair(0);
      }, delay);
    });

    window.GeethasCurrencyFix = {
      version: VERSION,
      refresh: () => {
        deriveRateFromProductPage();
        scheduleProductRepair(0);
        scheduleCartRepair(0);
      },
      getStatus: () => ({
        rate: state.rate,
        source: state.rateSource,
        rawProductInr: state.rawProductInr,
        liveProductNok: state.liveProductNok,
        productRoots: productRoots().length,
        cartPresent: Boolean(document.querySelector(CART_FORM)),
      }),
    };

    log('Started');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


/* Geethas — targeted Easify selected-price display patch
   Version 1.0
   Runs only on geethas.no.
   Purpose:
   - Open dropdown: hide duplicate "NOK" code, leaving e.g. "19 kr"
   - Closed selected field: mirror Currency Converter+'s already-converted NOK
     amount, e.g. "V [ +19 kr ]"
   - Does NOT modify Easify's real input value, selected state, cart maths,
     product prices, quantities, or checkout values.
*/
(() => {
  'use strict';

  const host = window.location.hostname.toLowerCase().replace(/^www\./, '');
  if (host !== 'geethas.no') return;

  const ROOT = '.tpo_option-set-wrapper';
  const INPUT = 'input.tpo_option-dropdown[readonly]';
  const BUTTON = 'button.tpo_option-type-selection';
  const overlays = new WeakMap();

  let busy = false;
  let timer = null;
  const observedRoots = new WeakSet();

  function hideNokCodes(root) {
    root.querySelectorAll('.currency-converter-wrapper-amount-box .cbb-price-code')
      .forEach((el) => {
        if (el.textContent.trim().toUpperCase() === 'NOK') {
          el.style.display = 'none';
        }
      });
  }

  function selectedButton(container, input) {
    const index = Number(input.dataset.selectedIndex);
    if (!Number.isInteger(index) || index < 0) return null;

    const buttons = [
      ...container.querySelectorAll(
        '.tpo_option-type-dropdown-wrapper ' + BUTTON
      )
    ];

    return buttons[index] || null;
  }

  function displayFromButton(button) {
    if (!button) return '';

    const label =
      button.querySelector('.tpo_option_type_label')?.textContent?.trim() || '';

    if (!label) return '';

    const digits =
      button.querySelector('.cbb-price-digits')?.textContent?.trim() || '';

    const symbol =
      button.querySelector('.cbb-price-symbol')?.textContent?.trim() || '';

    if (digits && symbol) {
      return `${label} [ +${digits} ${symbol} ]`;
    }

    return label;
  }

  function ensureOverlay(input, text) {
    const parent = input.parentElement;
    if (!parent || !text) return;

    parent.style.position = 'relative';

    let overlay = overlays.get(input);

    if (!overlay || !overlay.isConnected) {
      overlay = document.createElement('span');
      overlay.className = 'geethas-selected-nok notranslate';
      overlay.setAttribute('translate', 'no');

      Object.assign(overlay.style, {
        position: 'absolute',
        left: '12px',
        right: '35px',
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        zIndex: '10'
      });

      parent.appendChild(overlay);
      overlays.set(input, overlay);
    }

    overlay.textContent = text;

    // Keep Easify's actual value intact, but hide only its visual text.
    input.style.color = 'transparent';
    input.style.textShadow = 'none';
    input.style.background = 'transparent';
    input.style.caretColor = 'transparent';
  }

  function clearOverlay(input) {
    const overlay = overlays.get(input);
    if (overlay) overlay.remove();
    overlays.delete(input);

    input.style.removeProperty('color');
    input.style.removeProperty('text-shadow');
    input.style.removeProperty('background');
    input.style.removeProperty('caret-color');
  }

  function repairRoot(root) {
    if (!(root instanceof Element) || !root.isConnected) return;

    hideNokCodes(root);

    root.querySelectorAll(INPUT).forEach((input) => {
      const container = input.closest('.tpo_option-container');
      if (!container) return;

      const button = selectedButton(container, input);
      const display = displayFromButton(button);

      if (display) {
        ensureOverlay(input, display);
      } else {
        clearOverlay(input);
      }
    });
  }

  function repairAll() {
    timer = null;
    if (busy) return;
    busy = true;

    try {
      document.querySelectorAll(ROOT).forEach(repairRoot);
    } finally {
      busy = false;
    }
  }

  function schedule(delay = 40) {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(repairAll, delay);
  }

  function observeRoot(root) {
    if (!(root instanceof Element) || observedRoots.has(root)) return;
    observedRoots.add(root);

    const observer = new MutationObserver(() => {
      if (!busy) schedule(35);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function discoverRoots() {
    document.querySelectorAll(ROOT).forEach(observeRoot);
  }

  function onInteraction(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest(ROOT)) return;

    [20, 100, 300, 700].forEach((delay) => {
      window.setTimeout(() => schedule(0), delay);
    });
  }

  function start() {
    discoverRoots();

    [0, 100, 300, 800, 1500].forEach((delay) => {
      window.setTimeout(() => {
        discoverRoots();
        schedule(0);
      }, delay);
    });

    const rootDiscoveryObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches(ROOT) || node.querySelector(ROOT)) {
            discoverRoots();
            schedule(35);
            return;
          }
        }
      }
    });

    rootDiscoveryObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('click', onInteraction, true);
    document.addEventListener('change', onInteraction, true);

    window.GeethasEasifySelectedNok = {
      version: '1.0',
      refresh: () => {
        discoverRoots();
        schedule(0);
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
